import React, { useMemo, useState } from 'react'
import { useHistory, useParams, generatePath } from 'react-router-dom'
import { BreadcrumbView } from '../../context/breadcrumb.context'
import { Form, FormSubmitBack, FormTextConfirm } from '../form/form'
import { useParamsInt } from '../hooks/location'
import { Card } from '../card/card'
import { IconDelete } from '../icon/icon'
import { useNotificationContext } from '../../context/notification/context'
import { isLastOwner, refusedOrganizations, serverMessage } from '../error/server-message'
import { RefusalLinks } from '../error/last-owner-links'
import { ErrorReference } from '../error/error-reference'
import { useErrorLine } from '../error/use-error-line'

// A delete that fails says so, with the failed request's reference, and the
// page stays (owner 2026-10-07; it used to say nothing unless the page passed
// showServerError): a coded refusal's own message (e.g. a 409 for a record the
// API locks), anything else the page's fallback -- an uncoded body (old
// servers, raw database errors) is never shown.
// `lastOwnerLinks(organizations, form)`: on a last-owner refusal, the links
// shown after the message ([{to, text}]; see shared/error/last-owner-links).
// `heading(form)`, `description(form)` and `successMessage` say what this
// delete really does where "delete" undersells or oversells it (a member's
// "remove from organization", owner 2026-10-29); unset, the page reads as
// before.
export const Delete = ({ useApiForm, titleParam, successPath, onSuccess, lastOwnerLinks, heading, description, successMessage }) => {
  const { deleteID } = useParamsInt()

  const params = useParams()
  const history = useHistory()
  const apiForm = useApiForm()

  const notificationContext = useNotificationContext()

  const [error, failure, setError] = useErrorLine()
  const [links, setLinks] = useState(null)

  const title = useMemo(() => (heading ? heading(apiForm.form) : `delete ${apiForm.form[titleParam] || ''}`), [apiForm.form, titleParam, heading])

  const handleMatch = () => apiForm.get(deleteID)

  const handleSubmit = async () => {

    setError(null)
    setLinks(null)
    try {
      await apiForm.delete(deleteID)
      notificationContext.toast.notify(successMessage || 'delete success', 3000)
  
      if (onSuccess) {
        onSuccess(apiForm.form)
      } else {
        let path = generatePath(successPath, params)
        history.push(path)
      }
    } catch (err) {
      setError(serverMessage(err, "This couldn't be deleted."), err)
      if (lastOwnerLinks && isLastOwner(err)) setLinks(lastOwnerLinks(refusedOrganizations(err), apiForm.form))
    }
  }

  return (
    <React.Fragment>
      <BreadcrumbView Icon={IconDelete} text="delete" onMatch={handleMatch}>
        <Card title={title}>
          {description ? <div className="py-2">{description(apiForm.form)}</div> : <div className="py-2">This is a destructive action</div>}
          {error &&
            <p className="text-danger" role="alert">
              {error}
              <RefusalLinks links={links} />
            </p>}
          {error && <ErrorReference error={failure} className="mb-3" />}
          <Form state={apiForm.form} onSubmit={handleSubmit}>
            <FormTextConfirm placeholder="delete" subtext="Type delete to confirm" confirmValue="delete" name="test" required autoFocus />
            <FormSubmitBack text="delete" />
          </Form>
        </Card>
      </BreadcrumbView>
    </React.Fragment>
  )
}
